import LoggingCard from '@components/Settings/LoggingCard';
import Header from '@components/Shared/Layout/Header';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';
import { useChangePasswordMutation, useLogoutMutation } from '@store/api/authEndpoints';
import { useGetInstallationQuery } from '@store/api/installationEndpoints';
import { serverMessage } from '@store/error';
import { useState, type FormEvent } from 'react';

const SettingsPage = () => {
  const { data: installation } = useGetInstallationQuery();
  const [changePassword, { isLoading }] = useChangePasswordMutation();
  const [logout] = useLogoutMutation();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string>();
  const [done, setDone] = useState(false);

  const onChangePassword = async (event: FormEvent) => {
    event.preventDefault();
    setError(undefined);
    setDone(false);

    try {
      await changePassword({ currentPassword, newPassword }).unwrap();
      setCurrentPassword('');
      setNewPassword('');
      setDone(true);

      // Changing the password ends every session, this one included, because it is what somebody
      // does when they think a credential is compromised. The access token is a stateless JWT and
      // would keep this tab working for another fifteen minutes, so the panel signs itself out
      // rather than letting the person discover it a quarter of an hour later.
      setTimeout(() => { void logout(); }, 2000);
    } catch (caught) {
      setError(serverMessage(caught) ?? 'The password could not be changed.');
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Header title='Settings' description='Server information and administrator account.' />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>Administrator password</Typography>

          <form onSubmit={onChangePassword}>
            <Stack spacing={2} sx={{ maxWidth: 420 }}>
              <TextField
                label='Current password'
                type='password'
                value={currentPassword}
                autoComplete='current-password'
                onChange={(event) => setCurrentPassword(event.target.value)}
                required
              />
              <TextField
                label='New password'
                type='password'
                value={newPassword}
                autoComplete='new-password'
                onChange={(event) => setNewPassword(event.target.value)}
                required
              />

              {error && <Alert severity='error'>{error}</Alert>}
              {done && (
                <Alert severity='success'>
                  Password changed. Every session was signed out, including this one: sign in
                  again with the new password.
                </Alert>
              )}

              <Button type='submit' variant='contained' disabled={isLoading}>Change password</Button>
            </Stack>
          </form>
        </CardContent>
      </Card>

      <LoggingCard />

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant='h6' gutterBottom>Server</Typography>

          <Stack spacing={0.5}>
            <Typography variant='body2' color='text.secondary'>
              Version {installation?.serverVersion ?? '-'}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Installation id: <code>{installation?.installationId ?? '-'}</code>
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              Clients connect with the license activation id and password only. Point them at
              this server&apos;s address and nothing else is needed.
            </Typography>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Cloud synchronization</Typography>
          <Alert severity='info'>
            Not available yet. The server runs fully offline from its imported license files.
          </Alert>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SettingsPage;
